import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Loader2, Settings, Store, Bell, Smartphone, Banknote, RotateCcw, LayoutList, Send, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/layout/PageHeader";
import { useRestaurant } from "../context/RestaurantContext";
import { DemoDataCard } from "../components/settings/DemoDataCard";
import { BackgroundPhotosCard } from "../components/settings/BackgroundPhotosCard";
import { LogoGalleryCard } from "../components/settings/LogoGalleryCard";
import { RestaurantProfileForm } from "../components/settings/RestaurantProfileForm";
import RestaurantsSettings from "../components/settings/RestaurantsSettings";
import NotificationSettings from "../components/settings/NotificationSettings";
import ClosingReminderCard from "../components/settings/ClosingReminderCard";
import ConnectedDevicesCard from "../components/settings/ConnectedDevicesCard";
import TeamBroadcastCard from "../components/settings/TeamBroadcastCard";
import AccountantContactCard from "../components/settings/AccountantContactCard";
import AutoReorderConfigCard from "../components/settings/AutoReorderConfigCard";
import SupplierReminderConfigCard from "../components/settings/SupplierReminderConfigCard";
import ScorecardEmailConfigCard from "../components/settings/ScorecardEmailConfigCard";
import UserGuideCard from "../components/settings/UserGuideCard";
import SidebarModulesCard from "../components/settings/SidebarModulesCard";
import { ColorThemeSection } from "../components/theme/ColorThemeSection";
import CashRegistersCard from "../components/finance/CashRegistersCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import api from "../lib/api";
import { useImageGeneration } from "../context/ImageGenerationContext";
import { usePlanFeatures } from "../hooks/usePlanFeatures";
import { useSiteConfig } from "../hooks/useSiteConfig";
import { AdvancedBlock } from "../components/ui/advanced-block";

function SidebarOrderResetCard() {
  const [resetting, setResetting] = useState(false);
  const [hasCustomOrder, setHasCustomOrder] = useState(false);

  useEffect(() => {
    api.getMe()
      .then(me => {
        const order = me?.preferences?.sidebar_order || [];
        setHasCustomOrder(Array.isArray(order) && order.length > 0);
      })
      .catch(() => {});
  }, []);

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.updateMyPreferences({ sidebar_order: [] });
      setHasCustomOrder(false);
      toast.success("Orden del menú lateral restablecido");
      // Force a reload so DashboardLayout picks up the cleared preferences
      window.location.reload();
    } catch {
      toast.error("No se pudo restablecer el orden");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="rounded-xl border bg-card p-5 flex items-start gap-4"
      data-testid="sidebar-order-reset-card"
    >
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <LayoutList className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-base font-semibold mb-1">
          Menú lateral personalizado
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Has reordenado los apartados arrastrándolos. Puedes restablecer el
          orden por defecto en cualquier momento.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={resetting || !hasCustomOrder}
          data-testid="sidebar-order-reset-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {hasCustomOrder ? "Restablecer orden por defecto" : "Orden por defecto activo"}
        </Button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { currentRestaurant } = useOutletContext();
  const { refreshRestaurants } = useRestaurant();
  const { setRestaurantBgPhotos } = useImageGeneration();
  const [ready, setReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "profile");
  const { planId } = usePlanFeatures();
  const isStarter = planId === "plan_starter";
  const { userGuideVisible } = useSiteConfig();

  useEffect(() => {
    if (!currentRestaurant) return;
    api.getBackgroundPhotos(currentRestaurant.restaurant_id)
      .then(res => setRestaurantBgPhotos(res.photos || []))
      .catch(() => {});
    setReady(true);
  }, [currentRestaurant, setRestaurantBgPhotos]);

  const handleTabChange = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "profile") next.delete("tab");
    else next.set("tab", v);
    setSearchParams(next);
  };

  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <PageHeader
        icon={<Settings className="w-5 h-5" />}
        title="Configuración"
        description="Perfil del restaurante activo y gestión global de tus restaurantes."
        breadcrumb={[{ label: "Cuenta" }, { label: "Configuración" }]}
      />

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="profile" data-testid="settings-tab-profile">
            Perfil y branding
          </TabsTrigger>
          <TabsTrigger value="restaurants" data-testid="settings-tab-restaurants">
            <Store className="h-3.5 w-3.5 mr-1.5" /> Mis restaurantes
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">
            <Bell className="h-3.5 w-3.5 mr-1.5" /> Notificaciones
          </TabsTrigger>
          <TabsTrigger value="devices" data-testid="settings-tab-devices">
            <Smartphone className="h-3.5 w-3.5 mr-1.5" /> Dispositivos
          </TabsTrigger>
          {!isStarter && (
            <TabsTrigger value="cash-registers" data-testid="settings-tab-cash-registers">
              <Banknote className="h-3.5 w-3.5 mr-1.5" /> Cajas y turnos
            </TabsTrigger>
          )}
          {!isStarter && (
            <TabsTrigger value="auto-reorder" data-testid="settings-tab-auto-reorder">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Pedidos automáticos
            </TabsTrigger>
          )}
          {userGuideVisible && (
            <TabsTrigger value="help" data-testid="settings-tab-help">
              <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Ayuda
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="mt-6 space-y-6">
          {/* Esencial: lo que el 90% de usuarios necesita al configurar */}
          <RestaurantProfileForm
            currentRestaurant={currentRestaurant}
            onSaved={refreshRestaurants}
          />
          <LogoGalleryCard restaurantId={currentRestaurant.restaurant_id} />
          <BackgroundPhotosCard restaurantId={currentRestaurant.restaurant_id} />

          {/* Avanzado: personalización, contacto contable, atajos de setup */}
          <AdvancedBlock
            id="settings-profile-advanced"
            title="Personalización avanzada y datos técnicos"
            description="Tema visual, módulos activos, contable, datos demo…"
          >
            <ColorThemeSection />
            <SidebarModulesCard restaurantId={currentRestaurant.restaurant_id} />
            <AccountantContactCard restaurantId={currentRestaurant.restaurant_id} />
            <DemoDataCard
              restaurantId={currentRestaurant.restaurant_id}
              onRefresh={refreshRestaurants}
            />
            <SidebarOrderResetCard />
          </AdvancedBlock>
        </TabsContent>

        <TabsContent value="restaurants" className="mt-6">
          <RestaurantsSettings currentRestaurantId={currentRestaurant?.restaurant_id} />
        </TabsContent>

        <TabsContent value="notifications" className="mt-6 space-y-6">
          <NotificationSettings
            restaurantId={currentRestaurant?.restaurant_id}
            restaurantName={currentRestaurant?.name}
          />
          <ClosingReminderCard restaurantId={currentRestaurant?.restaurant_id} />
        </TabsContent>

        <TabsContent value="devices" className="mt-6 space-y-6">
          <ConnectedDevicesCard />
          <AdvancedBlock
            id="settings-devices-broadcast"
            title="Envío masivo al equipo"
            description="Broadcast de mensajes push a todos los miembros"
          >
            <TeamBroadcastCard restaurantId={currentRestaurant?.restaurant_id} />
          </AdvancedBlock>
        </TabsContent>

        {!isStarter && (
          <TabsContent value="cash-registers" className="mt-6">
            <CashRegistersCard restaurantId={currentRestaurant?.restaurant_id} />
          </TabsContent>
        )}

        {!isStarter && (
          <TabsContent value="auto-reorder" className="mt-6 space-y-6">
            <AutoReorderConfigCard restaurantId={currentRestaurant?.restaurant_id} />
            <AdvancedBlock
              id="settings-suppliers-advanced"
              title="Configuración avanzada de proveedores"
              description="Recordatorios semanales y emails de scorecard"
            >
              <SupplierReminderConfigCard restaurantId={currentRestaurant?.restaurant_id} />
              <ScorecardEmailConfigCard restaurantId={currentRestaurant?.restaurant_id} />
            </AdvancedBlock>
          </TabsContent>
        )}

        {userGuideVisible && (
          <TabsContent value="help" className="mt-6 space-y-6">
            <UserGuideCard />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
